<template>
	<v-modal>
		<template v-slot:header>
			<h1>Land tax calculation quote</h1>
		</template>
		<table class="mt-2">
			<tbody>
				<tr v-if="quote.calculation.client_type_fee">
					<th align="left" class="light" scope="row">Client type fee</th>
					<td align="right" v-html="$present('price', quote.calculation.client_type_fee)"/>
				</tr>
				<tr v-if="quote.calculation.purchase_type_fee">
					<th align="left" class="light" scope="row">Purchase type fee</th>
					<td align="right" v-html="$present('price', quote.calculation.purchase_type_fee)"/>
				</tr>
				<tr v-if="quote.calculation.purchase_fee">
					<th align="left" class="light" scope="row">Property fee</th>
					<td align="right" v-html="$present('price', quote.calculation.purchase_fee)"/>
				</tr>
				<tr v-if="quote.calculation.holding_fee">
					<th align="left" class="light" scope="row">Holding fee</th>
					<td align="right" v-html="$present('price', quote.calculation.holding_fee)"/>
				</tr>
				<tr v-if="quote.calculation.non_uk_resident_fee">
					<th align="left" class="light" scope="row">Non UK resident fee</th>
					<td align="right" v-html="$present('price', quote.calculation.non_uk_resident_fee)"/>
				</tr>
				<tr v-if="quote.calculation.submission_fee">
					<th align="left" class="light" scope="row">Compass submission fee</th>
					<td align="right" v-html="$present('price', quote.calculation.submission_fee)"/>
				</tr>
				<tr v-if="quote.calculation.expedite_fee">
					<th align="left" class="light" scope="row">Expedite calculation fee</th>
					<td align="right" v-html="$present('price', quote.calculation.expedite_fee)"/>
				</tr>
				<tr>
					<th align="right" class="beta fill" scope="row">Sub Total</th>
					<td align="right" class="beta heavy" v-html="$present('price', quote.calculation.subTotal)"/>
				</tr>
				<tr>
					<th align="right" class="beta fill" scope="row">VAT</th>
					<td align="right" class="beta heavy" v-html="$present('price', quote.calculation.tax)"/>
				</tr>
				<tr>
					<th align="right" class="alpha fill" scope="row">Total</th>
					<td align="right" class="alpha heavy" v-html="$present('price', quote.calculation.total)"/>
				</tr>
			</tbody>
		</table>
		<h2 class="mt-2 beta heavy">General Details</h2>
		<dl class="detail-list mt-1">
			<div class="detail-list__item">
				<dt>Client type</dt>
				<dd v-html="quote.service_requirements.type_of_client"/>
			</div>
			<div class="detail-list__item">
				<dt>Property price</dt>
				<dd v-html="$present('price', quote.service_requirements.property_price)"/>
			</div>
			<div class="detail-list__item--icon">
				<dt>Leasehold</dt>
				<dd>
					<bool-icon :value="quote.service_requirements.leasehold"/>
				</dd>
			</div>
			<div class="detail-list__item--icon">
				<dt>UK resident</dt>
				<dd>
					<bool-icon :value="quote.service_requirements.uk_resident"/>
				</dd>
			</div>
			<div class="detail-list__item--icon">
				<dt>Completing soon</dt>
				<dd>
					<bool-icon :value="quote.service_requirements.completing_soon"/>
				</dd>
			</div>
			<div v-if="quote.service_requirements.completing_soon" class="detail-list__item">
				<dt>Days until completion</dt>
				<dd v-html="quote.service_requirements.days_until_completion"/>
			</div>
		</dl>
		<h2 class="mt-2 beta heavy">Purchase Details</h2>
		<dl class="detail-list mt-1">
			<div v-if="quote.service_requirements.is_purchasing_single_home" class="detail-list__item--icon">
				<dt>Purchasing a single home</dt>
				<dd>
					<bool-icon :value="quote.service_requirements.is_purchasing_single_home"/>
				</dd>
			</div>
			<div v-if="quote.service_requirements.is_first_time_buyer" class="detail-list__item--icon">
				<dt>First time buyer</dt>
				<dd>
					<bool-icon :value="quote.service_requirements.is_first_time_buyer"/>
				</dd>
			</div>
			<div v-if="quote.service_requirements.purchase_is_shared_ownership" class="detail-list__item--icon">
				<dt>Purchase is shared ownership</dt>
				<dd>
					<bool-icon :value="quote.service_requirements.purchase_is_shared_ownership"/>
				</dd>
			</div>
			<div v-if="quote.service_requirements.is_purchasing_part_built_site" class="detail-list__item--icon">
				<dt>Buying a part built site</dt>
				<dd>
					<bool-icon :value="quote.service_requirements.is_purchasing_part_built_site"/>
				</dd>
			</div>
			<div v-if="quote.service_requirements.is_purchasing_commercial" class="detail-list__item--icon">
				<dt>Purchase is commercial</dt>
				<dd>
					<bool-icon :value="quote.service_requirements.is_purchasing_commercial"/>
				</dd>
			</div>
			<div v-if="quote.service_requirements.purchase_involves_divorce" class="detail-list__item--icon">
				<dt>Purchase involves a divorce</dt>
				<dd>
					<bool-icon :value="quote.service_requirements.purchase_involves_divorce"/>
				</dd>
			</div>
			<div v-if="quote.service_requirements.is_purchasing_multiple_properties" class="detail-list__item--icon">
				<dt>Buying multiple properties</dt>
				<dd>
					<bool-icon :value="quote.service_requirements.is_purchasing_multiple_properties"/>
				</dd>
			</div>
			<div v-if="quote.service_requirements.is_purchasing_subsale" class="detail-list__item--icon">
				<dt>Purchase is a subsale</dt>
				<dd>
					<bool-icon :value="quote.service_requirements.is_purchasing_subsale"/>
				</dd>
			</div>
			<div v-if="quote.service_requirements.purchase_has_connected_parties" class="detail-list__item--icon">
				<dt>Purchase has connected parties</dt>
				<dd>
					<bool-icon :value="quote.service_requirements.purchase_has_connected_parties"/>
				</dd>
			</div>
			<div v-if="quote.service_requirements.potential_mixed_use" class="detail-list__item--icon">
				<dt>Purchase might be mixed use</dt>
				<dd>
					<bool-icon :value="quote.service_requirements.potential_mixed_use"/>
				</dd>
			</div>
			<div v-if="quote.service_requirements.transfer_of_equity" class="detail-list__item--icon">
				<dt>Purchase involves a transfer of equity</dt>
				<dd>
					<bool-icon :value="quote.service_requirements.transfer_of_equity"/>
				</dd>
			</div>
			<div v-if="quote.service_requirements.probate" class="detail-list__item--icon">
				<dt>Purchase involves probate</dt>
				<dd>
					<bool-icon :value="quote.service_requirements.probate"/>
				</dd>
			</div>
			<div v-if="quote.service_requirements.is_purchasing_other" class="detail-list__item--icon">
				<dt>Purchase isn't described by any of the above</dt>
				<dd>
					<bool-icon :value="quote.service_requirements.is_purchasing_other"/>
				</dd>
			</div>
		</dl>
		<template v-slot:footer>
			<ul class="actions action--right">
				<li v-if="$can('update', quote)">
					<button
						class="button button--primary"
						@click.prevent="$vmodal.routeTo({name: 'quotes.edit', params: {quote: quote.id}})">Edit
					</button>
				</li>
			</ul>
		</template>
	</v-modal>
</template>
<script>
import Address from "./Address.vue";
import ConveyancingQuoteBreakdownTabbed from "./ConveyancingQuoteBreakdownTabbed";
import ConveyancingQuoteBreakdownTabs from "./ConveyancingQuoteBreakdownTabs.vue";

export default {
	components: {
		Address,
		ConveyancingQuoteBreakdownTabs,
		ConveyancingQuoteBreakdownTabbed,
	},

	props: {
		quote: {
			type: Object,
			required: true,
		},
	},

	methods: {
		feeName(feeIndex) {
			return feeIndex.toLowerCase()
				.replace("fee_", "")
				.replace("disbursement_", "")
				.split("_")
				.map((s) => s.charAt(0).toUpperCase() + s.substring(1))
				.join(" ");
		},
	},
};
</script>
